// 共通の型定義

// v2.0: 問題タイプ
export type QuestionType = "claim" | "preference";
export type ClaimType = "fact" | "value" | "policy";

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  claim: "主張を伝える",
  preference: "思いを伝える",
};

export const QUESTION_TYPE_DESCRIPTIONS: Record<
  QuestionType,
  { description: string; example: string }
> = {
  claim: {
    description: "議論・論証が可能な問い",
    example: "英語教育は必要か",
  },
  preference: {
    description: "好み・体験・感想など、正解のない問い",
    example: "ご飯派かパン派か",
  },
};

export const CLAIM_TYPE_LABELS: Record<ClaimType, string> = {
  fact: "事実",
  value: "価値",
  policy: "政策",
};

export const CLAIM_TYPE_DESCRIPTIONS: Record<ClaimType, { description: string; example: string }> =
  {
    fact: {
      description: "事実の解釈に基づく主張",
      example: "地球温暖化は人間活動が主因だ",
    },
    value: {
      description: "一般的な基準に基づく価値判断（良し悪し・優劣）",
      example: "インド料理は世界一の食文化だ",
    },
    policy: {
      description: "行動・政策の提案",
      example: "プログラミングを小学校で必修にすべきか",
    },
  };

export const QUESTION_TYPES: readonly QuestionType[] = ["claim", "preference"] as const;
export const CLAIM_TYPES: readonly ClaimType[] = ["fact", "value", "policy"] as const;

// 登録状況の集計カテゴリ（アプリのホーム画面・分析タブのグルーピングと同じ3種＋未設定）。
// claimWithSourceText は questionType ではなく、課題文（sourceText）が非空という派生カテゴリ。
export type TopicStatsCategory = "preference" | "claim" | "claimWithSourceText" | "unknown";

export const TOPIC_STATS_CATEGORIES: readonly TopicStatsCategory[] = [
  "preference",
  "claim",
  "claimWithSourceText",
  "unknown",
] as const;

export const TOPIC_STATS_CATEGORY_LABELS: Record<TopicStatsCategory, string> = {
  preference: "思いを伝える",
  claim: "主張を伝える",
  claimWithSourceText: "課題文あり",
  unknown: "未設定",
};

export interface OpinionPattern {
  typeLabel: string;
  example: string;
  description: string;
}

export interface Topic {
  id?: string;
  genre: string;

  difficulty: number;
  theme: string;
  // 課題文（claimWithSourceText カテゴリの読解用テキスト）。非空なら「主張を伝える（課題文あり）」
  sourceText?: string;
  claim?: string;
  grounds?: string;
  opinionPatterns: OpinionPattern[];
  isPublished: boolean;
  version: number;
  createdAt: string | Date;
  updatedAt: string | Date;
  createdBy: string;
  tags: string[];
  // v1.3追加: イメージ画像URL
  imageUrl?: string;
  // v1.4追加: X投稿URL
  xPostUrl?: string;
  // v2.0追加: 問題タイプ
  questionType?: QuestionType;
  claimType?: ClaimType;
  background?: string;
  // v2.3追加: テーマを深掘りする解説テキストと参考URL（表示専用。評価の入力には使わない）
  learnMore?: string;
  references?: TopicReference[];
  // v2.7追加: 賞味期限があるお題か（true=時間が経つと問いが成立しない / false=いつ出しても成立する）。
  // 未設定はフラグ導入前のお題で、判定がまだ付いていないことを表す
  isTopical?: boolean;
}

// 参考URL（topics.references の要素）
export interface TopicReference {
  title: string;
  url: string;
}

// 後方互換性のためのエイリアス（徐々にTopicに移行）
export type Opinion = Topic;

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface TopicListResponse {
  data: Topic[];
  pagination: PaginationInfo;
}

// フィルター用の型
export interface OpinionFilters {
  difficulty?: number;
  genre?: string;
  isPublished?: boolean;
  questionType?: QuestionType;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// フォーム用の型
export interface TopicFormData {
  genre: string;
  difficulty: number;
  theme: string;
  // 課題文（claimWithSourceText カテゴリの読解用テキスト）
  sourceText?: string;
  opinionPatterns: OpinionPattern[];
  isPublished: boolean;
  tags: string[];
  xPostUrl?: string;
  // v2.0追加
  questionType?: QuestionType;
  claimType?: ClaimType;
  background?: string;
  // v2.3追加
  learnMore?: string;
  references?: TopicReference[];
  // v2.7追加: 賞味期限があるお題か
  isTopical?: boolean;
}

// 後方互換性のためのエイリアス
export type OpinionFormData = TopicFormData;

// AI生成用の型（旧API - 後方互換性）
export interface GenerateOpinionRequest {
  genre: string;
  difficulty: number;
  note?: string; // 任意：作成したい問題の補足情報
}

export interface BulkAddRequest {
  theme: string;
  genre: string;
  difficulty: number;
  count: number;
}

// 一括生成用の型
export interface BulkGenerateRequest {
  count: number; // 生成する問題数
  genre: string;
  difficulty: number;
  note?: string; // 任意：作成したい問題の補足情報
}

export interface BulkGenerateResponse {
  success: boolean;
  opinion?: Topic;
  error?: string;
}

// タグマスター
export interface Tag {
  id: string;
  name: string;
  createdAt: string | Date;
}

// 定数
export const GENRES = [
  "dailylife",
  "relationships",
  "lifestyle",
  "health",
  "parenting",
  "business",
  "career",
  "learning",
  "entertainment",
  "technology",
  "psychology",
  "culture",
  "history",
  "science",
  "medicine",
  "environment",
  "politics",
  "philosophy",
] as const;

export type Genre = (typeof GENRES)[number];

export const GENRE_LABELS: Record<Genre, string> = {
  dailylife: "日常生活",
  relationships: "人間関係",
  lifestyle: "ライフスタイル",
  health: "健康",
  parenting: "子育て",
  business: "ビジネス",
  career: "キャリア",
  learning: "学習",
  entertainment: "エンタメ",
  technology: "テクノロジー",
  psychology: "心理",
  culture: "文化",
  history: "歴史",
  science: "科学",
  medicine: "医療",
  environment: "環境",
  politics: "政治経済",
  philosophy: "哲学",
};

// お題の難易度。2026-09-09 以降、採点には影響しない（評価バージョン 3.0.0 は全員共通のルーブリック）。
// 出題側の目安として残す。役割はテーマの重さを散らすことだけで、本文の分量には使わない
// （2026-09-10 に字数から難易度の段付けを外し、全件共通の上限のみにした）。
// 値そのものは evaluationVersion 2.2.0 の既存お題が採点に使うため引き続き必要。
export const DIFFICULTIES = [1, 2, 3, 4, 5] as const;

export const DIFFICULTY_LABELS = {
  1: "入門",
  2: "初級",
  3: "中級",
  4: "上級",
  5: "達人",
} as const;

// 本文の字数は上限だけを定める（下限は置かない）。
// 2026-09-10 に難易度別の段付けと下限を全廃した。下限は目標値として働き、水増しを生んでいたため。
// 生成ルールの真実源は docs/operation/weekly-topic-generation-rules.md で、ここはその値に揃える。

// 意見パターンの具体例の上限文字数
export const OPINION_PATTERN_EXAMPLE_MAX_CHAR_COUNT = 350;

// 意見パターンのラベルの推奨文字数範囲
export const OPINION_PATTERN_LABEL_RECOMMENDED_CHAR_COUNT = {
  min: 5,
  max: 15,
} as const;

// 意見パターンの説明の上限文字数
export const OPINION_PATTERN_DESCRIPTION_MAX_CHAR_COUNT = 70;

// 問題別統計情報
export interface TopicStats {
  count: number;
  viewCount: number;
  genre: string;
  difficulty: number;
  contentPreview: string;
  theme?: string;
}

// 期間別統計の共通型
export interface PeriodStats {
  totalResponses: number;
  totalLaunches: number;
  uniqueLaunches: number;
  totalViews: number;
  byGenre: { [genre: string]: number };
  byDifficulty: { [difficulty: number]: number };
  byTopic: { [topicId: string]: TopicStats };
}

// 統計関連の型定義
export interface DashboardStats {
  today: PeriodStats & { date: string };
  yesterday: PeriodStats & { date: string };
  thisWeek: PeriodStats & { weekLabel: string; startDate: string; endDate: string };
  lastWeek: PeriodStats & { weekLabel: string; startDate: string; endDate: string };
  thisMonth: PeriodStats & { month: string };
  lastMonth: PeriodStats & { month: string };
  lastUpdated: string;
}

export interface StatsTimeSeries {
  dateRange: {
    from: string;
    to: string;
  };
  dailyData: Array<{
    date: string;
    totalResponses: number;
    byGenre: { [genre: string]: number };
    byDifficulty: { [difficulty: number]: number };
    byTopic: { [topicId: string]: TopicStats };
  }>;
}

export type Difficulty = (typeof DIFFICULTIES)[number];

// v1.3: 週間問題設定の型定義
export interface WeeklyTopics {
  id: string; // ドキュメントID（例: "2024-W03"）
  weekStartDate: string | null;
  weekEndDate: string | null;
  topicIds: string[];
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string;
}

export interface WeeklyTopicsWithDetails extends WeeklyTopics {
  topics: Topic[];
}

export interface WeekOption {
  weekId: string;
  weekStartDate: string;
  weekEndDate: string;
  label: string;
  hasData: boolean;
}

// 管理者ユーザー管理
export type AdminUserRole = "admin";

export interface AdminUser {
  uid: string;
  email: string;
  displayName: string | null;
  role: AdminUserRole;
  disabled: boolean;
  createdAt: string;
  lastSignInTime: string | null;
}

export interface CreateAdminUserRequest {
  email: string;
  password: string;
  displayName?: string;
  role: AdminUserRole;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  total: number;
}

// 評価結果関連の型定義

// 評価データバージョン（管理画面が詳細ビューを持っているバージョン）。
// v1系（v1 / v1.3.0 / v1.4.0）は表示対応を終了したため含めない。
export type EvaluationVersion = "v2.0.0" | "v2.1.0" | "v2.2.0" | "v3.0.0";

// 管理画面がレコードを解釈するときのデータ形式。
// "unsupported" は、詳細ビューを持たないバージョン（対応表に無い evaluationVersion、
// もしくは evaluationVersion を持たない v1系レガシー）を指す。
export type EvaluationDataFormat = EvaluationVersion | "unsupported";

// v2.0.0 評価結果（ネスト構造）
export interface EvaluationResultV2_0_0 {
  understanding: {
    scores: { understanding: number };
    scoreExplanations: { understanding: string };
    reasoning?: { understandingChecklist?: string };
  };
  logicOriginality: {
    scores: { logic: number; originality: number };
    scoreExplanations: { logic: string; originality: string };
    reasoning?: { logicChecklist?: string; originalityChecklist?: string };
  };
  structureExpression: {
    scores: { structure: number; expression: number };
    scoreExplanations: { structure: string; expression: string };
    reasoning?: { structureChecklist?: string; expressionChecklist?: string };
  };
  improvement: {
    shortReview: string;
    improvements: string[];
    strengths: string[];
    improvedExample: string;
  };
  questionType?: QuestionType;
  claimType?: ClaimType | null;
  totalScore: number;
}

// v2.1.0 採点済みチェックリスト項目
// サーバ側 functions/src/prompts/v2.1.0/types.ts の ScoredChecklistItemV2_1 と一致
export interface ScoredChecklistItemV2_1 {
  item: string;
  reason: string;
  point: number;
  maxPoint: number;
}

// v2.1.0 改善ポイントの粒度
export type ImprovementScopeV2_1 = "partial" | "structural";

// v2.1.0 改善ポイント（構造化）
// サーバ側 functions/src/prompts/v2.1.0/types.ts の Improvement と一致
export interface ImprovementV2_1 {
  title: string;
  scope: ImprovementScopeV2_1;
  targetQuote: string | null;
  explanation: string;
  rewriteExample: string | null;
}

// v2.1.0 評価結果（ネスト構造 + 採点済みチェックリスト + 構造化改善ポイント）
export interface EvaluationResultV2_1_0 {
  understanding: {
    scores: { understanding: number };
    scoreExplanations: { understanding: string };
    reasoning?: { understandingScoredChecklist?: ScoredChecklistItemV2_1[] };
  };
  logicOriginality: {
    scores: { logic: number; originality: number };
    scoreExplanations: { logic: string; originality: string };
    reasoning?: {
      logicScoredChecklist?: ScoredChecklistItemV2_1[];
      originalityScoredChecklist?: ScoredChecklistItemV2_1[];
    };
  };
  structureExpression: {
    scores: { structure: number; expression: number };
    scoreExplanations: { structure: string; expression: string };
    reasoning?: {
      structureScoredChecklist?: ScoredChecklistItemV2_1[];
      expressionScoredChecklist?: ScoredChecklistItemV2_1[];
    };
  };
  improvement: {
    shortReview: string;
    improvements: ImprovementV2_1[];
    strengths: string[];
    improvedExample: string;
  };
  questionType?: QuestionType;
  claimType?: ClaimType | null;
  totalScore: number;
}

// v2.2.0 採点済みチェックリスト項目
// サーバ側 functions/src/prompts/v2.2.0/types.ts の ScoredChecklistItemV2_2 と一致
// バージョン分離ルール: v2.1.0 の型を再利用せず、v2.2.0 用に自前で持つ
export interface ScoredChecklistItemV2_2 {
  item: string;
  reason: string;
  point: number;
  maxPoint: number;
}

// v2.2.0 改善ポイントの粒度
export type ImprovementScopeV2_2 = "partial" | "structural";

// v2.2.0 改善ポイントの優先度（改善効果の大きさ）。v2.2.0 で追加
export type ImprovementPriorityV2_2 = "high" | "medium" | "low";

// v2.2.0 改善ポイント（構造化）
// サーバ側 functions/src/prompts/v2.2.0/types.ts の Improvement と一致
export interface ImprovementV2_2 {
  title: string;
  scope: ImprovementScopeV2_2;
  priority: ImprovementPriorityV2_2;
  targetQuote: string | null;
  explanation: string;
  rewriteExample: string | null;
}

// v2.2.0 評価に使った LLM のモデル名（軸ごとにモデルを変えられるため軸単位で保持）
// サーバ側 functions/src/prompts/v2.2.0/evaluationService.ts の EvaluationOutput.llmModels と一致
export interface EvaluationLlmModelsV2_2_0 {
  understandingScoring: string;
  logicOriginalityScoring: string;
  structureExpressionScoring: string;
  improvement: string;
}

// v2.2.0 評価結果（ネスト構造 + 採点済みチェックリスト + 優先度つき改善ポイント）
export interface EvaluationResultV2_2_0 {
  understanding: {
    scores: { understanding: number };
    scoreExplanations: { understanding: string };
    reasoning?: { understandingScoredChecklist?: ScoredChecklistItemV2_2[] };
  };
  logicOriginality: {
    scores: { logic: number; originality: number };
    scoreExplanations: { logic: string; originality: string };
    reasoning?: {
      logicScoredChecklist?: ScoredChecklistItemV2_2[];
      originalityScoredChecklist?: ScoredChecklistItemV2_2[];
    };
  };
  structureExpression: {
    scores: { structure: number; expression: number };
    scoreExplanations: { structure: string; expression: string };
    reasoning?: {
      structureScoredChecklist?: ScoredChecklistItemV2_2[];
      expressionScoredChecklist?: ScoredChecklistItemV2_2[];
    };
  };
  improvement: {
    shortReview: string;
    improvements: ImprovementV2_2[];
    strengths: string[];
    improvedExample: string;
  };
  questionType?: QuestionType;
  claimType?: ClaimType | null;
  totalScore: number;
}

// v3.0.0 採点済みチェックリスト項目
// サーバ側 functions/src/prompts/v3.0.0/types.ts の ScoredChecklistItemV3_0 と一致
// バージョン分離ルール: v2.2.0 の型を再利用せず、v3.0.0 用に自前で持つ
export interface ScoredChecklistItemV3_0 {
  item: string;
  reason: string;
  point: number;
  maxPoint: number;
  /** 内容の項目だけが持つ、判定の根拠としてユーザーの文章から抜き出した箇所（無ければ「なし」） */
  evidence?: string;
}

// v3.0.0 の採点軸。テーマ型は内容の軸と構成、表現力。課題文型は読解が加わる
// サーバ側 functions/src/prompts/v3.0.0/types.ts の EvaluationAxisKindV3_0 と一致
export type EvaluationAxisKindV3_0 =
  | "reading"
  | "response"
  | "depiction"
  | "emotion"
  | "interpretation"
  | "claim"
  | "warrant"
  | "grounds"
  | "perspective"
  | "structure"
  | "expression";

// v3.0.0 採点結果の1軸。軸ごとの満点はカテゴリで変わるため、保存値の maxScore をそのまま使う
export interface EvaluationAxisResultV3_0 {
  axis: EvaluationAxisKindV3_0;
  label: string;
  score: number;
  maxScore: number;
  explanation: string;
  scoredChecklist: ScoredChecklistItemV3_0[];
}

// v3.0.0 改善ポイントの粒度
export type ImprovementScopeV3_0 = "partial" | "structural";

// v3.0.0 改善ポイントの優先度（改善効果の大きさ）
export type ImprovementPriorityV3_0 = "high" | "medium" | "low";

// v3.0.0 改善ポイント
// サーバ側 functions/src/prompts/v3.0.0/types.ts の Improvement と一致
export interface ImprovementV3_0 {
  title: string;
  scope: ImprovementScopeV3_0;
  priority: ImprovementPriorityV3_0;
  targetQuote: string | null;
  explanation: string;
  rewriteExample: string | null;
}

// v3.0.0 評価に使った LLM のモデル名。読解は課題文型だけ採点するため、それ以外は null
// サーバ側 functions/src/prompts/v3.0.0/evaluationService.ts の EvaluationOutput.llmModels と一致
export interface EvaluationLlmModelsV3_0_0 {
  readingScoring: string | null;
  contentScoring: string;
  structureExpressionScoring: string;
  improvement: string;
}

// v3.0.0 評価結果（軸の配列 + 優先度つき改善ポイント）
export interface EvaluationResultV3_0_0 {
  questionType?: QuestionType;
  claimType?: ClaimType | null;
  /** お題カテゴリ。軸の構成（読解の有無）と内容の配点はこれで決まる */
  category?: RubricCategoryKind;
  /** 採点軸。並びは 読解（課題文型のみ）、内容の軸、構成、表現力 */
  axes: EvaluationAxisResultV3_0[];
  improvement: {
    shortReview: string;
    improvements: ImprovementV3_0[];
    strengths: string[];
    improvedExample: string;
  };
  totalScore: number;
}

// 評価一覧アイテム
export interface EvaluationListItem {
  id: string;
  version: EvaluationDataFormat;
  timestamp: { _seconds: number };
  topic: {
    id: string;
    content: string;
    genre: string;
    difficulty: number;
    theme: string;
    questionType?: QuestionType;
    claimType?: ClaimType;
    background?: string;
  };
  userOpinionPreview: string;
  totalScore: number;
  appVersion: string;
  /** Firestore に保存された evaluationVersion の生値（v1 系は未保存のため undefined） */
  evaluationVersion?: string;
  /** Firestore に保存された promptVersion の生値（評価バージョンをまたいで単調増加する通し番号） */
  promptVersion?: number;
}

export interface EvaluationDetailV2_0_0 {
  version: "v2.0.0";
  id: string;
  timestamp: { _seconds: number };
  topic: {
    id: string;
    theme: string;
    background?: string;
    genre: string;
    difficulty: number;
    content?: string;
    questionType?: QuestionType;
    claimType?: ClaimType | null;
  };
  input: {
    userOpinion: string;
    understanding?: {
      claim: string;
      grounds: string;
    };
  };
  result: EvaluationResultV2_0_0;
  appVersion: string;
  promptVersion?: number;
}

export interface EvaluationDetailV2_1_0 {
  version: "v2.1.0";
  id: string;
  timestamp: { _seconds: number };
  topic: {
    id: string;
    theme: string;
    background?: string;
    genre: string;
    difficulty: number;
    content?: string;
    questionType?: QuestionType;
    claimType?: ClaimType | null;
  };
  input: {
    userOpinion: string;
    understanding?: {
      claim: string;
      grounds: string;
    };
  };
  result: EvaluationResultV2_1_0;
  appVersion: string;
  promptVersion?: number;
}

export interface EvaluationDetailV2_2_0 {
  version: "v2.2.0";
  id: string;
  timestamp: { _seconds: number };
  topic: {
    id: string;
    theme: string;
    background?: string;
    genre: string;
    difficulty: number;
    content?: string;
    questionType?: QuestionType;
    claimType?: ClaimType | null;
  };
  input: {
    userOpinion: string;
    understanding?: {
      claim: string;
      grounds: string;
    };
  };
  result: EvaluationResultV2_2_0;
  appVersion: string;
  promptVersion?: number;
  /** v2.2.0 で追加。軸ごとにどのモデルで採点したかを記録している */
  llmModels?: EvaluationLlmModelsV2_2_0;
}

export interface EvaluationDetailV3_0_0 {
  version: "v3.0.0";
  id: string;
  timestamp: { _seconds: number };
  topic: {
    id: string;
    theme: string;
    background?: string;
    genre: string;
    difficulty: number;
    content?: string;
    questionType?: QuestionType;
    claimType?: ClaimType | null;
  };
  input: {
    userOpinion: string;
    understanding?: {
      claim: string;
      grounds: string;
    };
  };
  result: EvaluationResultV3_0_0;
  appVersion: string;
  promptVersion?: number;
  llmModels?: EvaluationLlmModelsV3_0_0;
}

// 評価詳細（バージョン別の判別共用体）
export type EvaluationDetail =
  | EvaluationDetailV2_0_0
  | EvaluationDetailV2_1_0
  | EvaluationDetailV2_2_0
  | EvaluationDetailV3_0_0;

// 評価フィルター
export interface EvaluationFilters {
  startDate?: Date;
  endDate?: Date;
  difficulty?: number;
}

// 週次プッシュ通知（weeklyPushNotifications/{weekId}）
// 週次パイプラインがその週のお題から作り、月曜8時（JST）に1通だけ送る
export interface WeeklyPushNotification {
  weekId: string;
  title: string;
  body: string;
  topicId: string; // タイトルの問いに使ったお題
  scheduledAt: string; // 送信予定時刻（ISO形式）
  sentAt: string | null; // 送信済み時刻（ISO形式）。未送信は null
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// プロンプト確認
export type PromptPreviewCategory = "preference" | "claim" | "claimWithSourceText";

export const PROMPT_PREVIEW_CATEGORY_LABELS: Record<PromptPreviewCategory, string> = {
  preference: "思いを伝える",
  claim: "主張を伝える",
  claimWithSourceText: "主張を伝える（課題文あり）",
};

export type PromptPreviewClaimType = "fact" | "value" | "policy";

export const PROMPT_PREVIEW_CLAIM_TYPE_LABELS: Record<PromptPreviewClaimType, string> = {
  fact: "fact（事実）",
  value: "value（価値）",
  policy: "policy（政策）",
};

export interface PromptPreviewAxisPrompt {
  /** v3.0.0 の呼び出し単位。読解は課題文型だけに出る */
  axis: "reading" | "content" | "structureExpression" | "improvement";
  label: string;
  prompt: { system: string; user: string };
}

export interface PromptPreviewResponse {
  evaluationVersion: string;
  promptVersion: number;
  category: PromptPreviewCategory;
  claimType?: PromptPreviewClaimType;
  prompts: PromptPreviewAxisPrompt[];
}

// === 採点ルーブリック（v2.1.0） ===
// サーバ側 functions/src/prompts/v2.1.0/rubricCatalog.ts の型を正確に写したもの。
// 実際に採点プロンプトへ埋め込まれている定義そのものを配信APIから受け取る。

// お題カテゴリ（ホーム画面のグルーピングと同じ3種）
export type RubricCategoryKind = "preference" | "claim" | "claimWithSourceText";

// 採点プロンプトの軸（1回のLLM呼び出し単位）。v3.0.0 は 読解（課題文型のみ） / 内容 / 構成と表現力
export type RubricAxisKind = "reading" | "content" | "structureExpression";

// 採点方式。checklist は項目ごとの4段階加点、band は得点帯（バンド）方式
export type RubricScoringMethod = "checklist" | "band";

// チェックリスト項目の1段階（満点/概ね加点/部分加点/加点なし）
export interface ChecklistRubricLevel {
  point: number;
  description: string;
}

// チェックリスト項目の定義（判定の段階は項目ごとに2〜4）
export interface ChecklistItemDef {
  item: string;
  full: ChecklistRubricLevel;
  // 3段階以上の項目だけが持つ
  most?: ChecklistRubricLevel;
  // 4段階の項目だけが持つ
  partial?: ChecklistRubricLevel;
  none: ChecklistRubricLevel;
  /** この項目を満たす書き方の固定例（未執筆の項目では undefined） */
  goodExample?: string;
}

// ルーブリックの1バンド（得点帯）
export interface RubricBand {
  min: number;
  max: number;
  description: string;
}

// 配信用のルーブリックセクション（例: 「論理性」「課題文の読解」）
// 方式に応じて checklistItems か bands のどちらかを持つ
export interface RubricSectionData {
  label: string;
  maxPoints: number;
  method: RubricScoringMethod;
  checklistItems?: ChecklistItemDef[];
  bands?: RubricBand[];
}

export interface RubricAxisData {
  axis: RubricAxisKind;
  axisLabel: string;
  maxPoints: number;
  sections: RubricSectionData[];
}

export interface RubricDifficultyData {
  difficulty: number;
  axes: RubricAxisData[];
}

/**
 * カテゴリごとのルーブリック。配信バージョンで形が2通りある（どちらか一方だけが入る）。
 * - v2.1.0 / v2.2.0: 難易度ごとに別々の項目セットを持つため `byDifficulty`
 * - v3.0.0 以降: 全員共通のルーブリックなので難易度の階層がなく `axes`
 */
export interface RubricCategoryData {
  category: RubricCategoryKind;
  categoryLabel: string;
  byDifficulty?: RubricDifficultyData[];
  axes?: RubricAxisData[];
}

/**
 * 採点ルーブリックの配信バージョン（お題の topics.evaluationVersion に対応）。
 * Functions 側に配信ルート（/rubrics/v<版>）があるものを全パターン明示列挙する。
 */
export type RubricEvaluationVersion = "3.0.0";

export interface RubricCatalog {
  promptVersion: string;
  categories: RubricCategoryData[];
}
